import React, { useEffect, useMemo, useState, useRef } from 'react'
import styled from 'styled-components'
import { useAppDispatch } from 'src/store'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useAppSelector, RootState } from 'src/store'

// Utils
import { createEchoConnection } from 'src/utils/echoService'
// Hooks
import useScrollToView from 'src/hook/useScrollToView'
import usePrevious from 'src/hook/usePrevious'
// Actions
import { getMessagesRequest, resetUnread } from 'src/useCase/message'
import { getChannelRequest } from 'src/useCase/channel'
import { actions } from 'src/store/domain/chat/message'
import { actions as channelActions } from 'src/store/domain/chat/channel'
// Selectors
import {
  selectMessages,
  selectLastPage,
  selectIsFetchingMessage,
  selectScrollToBottom,
} from 'src/store/domain/chat/message/selector'
import { selectIsFetchingChannel } from 'src/store/domain/chat/channel/selector'

// Components
import NewMessageForm from './NewMessageForm'
import MessageItem from './MessageItem'
// Constants
import {
  SOCKET_EVENT,
  SENDER_BY,
  MESSAGE_TYPE,
} from 'src/containers/Chat/constants'
// Icons
import Spinner from 'src/assets/icons/spinner.svg'
import LocalStorage from 'src/utils/LocalStorage'
import { useSearchParams } from 'react-router-dom'
import { paramsType } from 'src/containers/MyPage/Treatment'
import { USER_TREATMENT_STATE } from 'src/enum/userTreatmentState'
import { getUserTreatmentList } from 'src/useCase/userTreatment'
import { UserTreatmentInterface } from 'src/interfaces/userTreatment'

const ChatContainer: React.FC = () => {
  const messages = useAppSelector((state: RootState) => selectMessages(state))
  const lastPage = useAppSelector((state: RootState) => selectLastPage(state))
  const isFetchingMessage = useAppSelector((state: RootState) =>
    selectIsFetchingMessage(state),
  )
  const isFetchingChannel = useAppSelector((state: RootState) =>
    selectIsFetchingChannel(state),
  )
  const scrollToBottom = useAppSelector((state: RootState) =>
    selectScrollToBottom(state),
  )
  const [searchParams] = useSearchParams()

  const prevCountMessage: any = usePrevious(messages.length)
  const channelId = useRef<any>(null)
  const dispatch = useAppDispatch()

  const user = useAppSelector((state) => state.ui.app.authInfo)
  const userTreatmentId =
    searchParams.get('userTreatmentId') ??
    searchParams.get('user_treatment_id') ??
    LocalStorage.getUserTreatmentId()

  const userTreatments = useAppSelector(
    (state: RootState) => state.domain.myPage.userTreatment.entities,
  ) as any[] | null | undefined

  const selectedUserTreatment = useMemo(() => {
    if (!userTreatmentId || !userTreatments?.length) return null

    return (
      (userTreatments.find(
        (item) => Number(item.id) === Number(userTreatmentId),
      ) as UserTreatmentInterface) || null
    )
  }, [userTreatmentId, userTreatments])

  const selectedUserTreatmentRef = useRef<UserTreatmentInterface | null>(null)
  useEffect(() => {
    selectedUserTreatmentRef.current = selectedUserTreatment
  }, [selectedUserTreatment])

  const [pagination, setPagination] = useState({
    page: 1,
    per_page: 20,
    order: '-id',
  })
  const [activeItem, setActiveItem] = useState(null)
  const { stwRefs } = useScrollToView({
    items: messages,
    activeItem: scrollToBottom ? activeItem : null,
    key: 'id',
    options: {
      behavior: 'smooth',
    },
  })

  useEffect(() => {
    getUserTreatments()
    return () => {
      dispatch(actions.resetMessageStore())
    }
  }, [])

  useEffect(() => {
    if (user?.id) {
      const channelName = `private-user.${user?.id}`
      const socket = socketListen(channelName)
      return () => {
        socket.leaveChannel(channelName)
      }
    }
  }, [user?.id])

  useEffect(() => {
    getMessages({ isInitial: pagination.page === 1 })
  }, [pagination.page])

  useEffect(() => {
    if (messages.length > prevCountMessage) {
      setActiveItem(messages[0]?.id)
      return
    }
    setActiveItem(null)
  }, [messages, prevCountMessage])

  const hasMore = useMemo(() => {
    return pagination.page < lastPage
  }, [pagination.page, lastPage])

  const handleMessageCreated = (data: any) => {
    const currentSelected = selectedUserTreatmentRef.current
    const isCurrentChannel = data.channel_id === channelId.current?.id

    const hasReservationInTreatment = Boolean(
      currentSelected &&
        (Number(currentSelected.reservation_id) ===
          Number(data.reservation_id) ||
          currentSelected.reservations?.some(
            (r) => Number(r.id) === Number(data.reservation_id),
          )),
    )

    if (!isCurrentChannel || !hasReservationInTreatment) return

    if (
      data.sender_by === SENDER_BY.MANAGER &&
      data.type_message === MESSAGE_TYPE.BUTTON
    ) {
      getUserTreatments()
    }

    dispatch(
      resetUnread({
        channelId: data.channel_id,
        data: {
          user_treatment_id: userTreatmentId
            ? Number(userTreatmentId)
            : undefined,
        },
      }),
    )
    messageCreated(data)
  }

  const socketListen = (channelName: string) => {
    const echo = createEchoConnection()
    echo
      .channel(channelName)
      .listen(SOCKET_EVENT.MESSAGE_CREATED, ({ data }: { data: any }) => {
        handleMessageCreated(data)
      })
      .listen(SOCKET_EVENT.MESSAGE_READ, ({ data }: { data: any }) => {
        const countMessageRead = data?.message_recipient?.message_read ?? 0
        dispatch(actions.setCountMessageRead(countMessageRead))
      })
      .listen(SOCKET_EVENT.MESSAGE_DELETED, ({ data }: { data: any }) => {
        dispatch(actions.deleteMessage({ id: data.message_id }))
      })
      .listen(SOCKET_EVENT.MESSAGE_UPDATED, ({ data }: { data: any }) => {
        dispatch(actions.updateMessage(data))
      })
      .listen(
        SOCKET_EVENT.DOCTOR_FINISH_CHAT,
        ({ channel }: { channel: any }) => {
          dispatch(
            channelActions.updateChannel({ expired_at: channel.expired_at }),
          )
        },
      )
    return echo
  }

  const messageCreated = (data) => {
    if (
      data.sender_by === SENDER_BY.USER &&
      data.type_message === MESSAGE_TYPE.TEXT
    ) {
      const payload = {
        ...data,
        key: 'is_temporary',
        is_temporary: false,
      }
      dispatch(actions.updateMessage(payload))
      dispatch(actions.setIsSending(false))
      return
    }
    if (
      data.sender_by === SENDER_BY.MANAGER &&
      (data.type_message === MESSAGE_TYPE.TEXT ||
        data.type_message === MESSAGE_TYPE.IMAGE ||
        data.type_message === MESSAGE_TYPE.BUTTON ||
        data.type_message === MESSAGE_TYPE.PLAN)
    ) {
      if (data.type_message === MESSAGE_TYPE.PLAN) {
        getChannelDetail()
      }
      dispatch(actions.createMessage(data))
      return
    }
  }

  const getMessages = async ({ isInitial }) => {
    if (isInitial) {
      const payload = await getChannelDetail()
      if (!payload) return
      await dispatch(
        resetUnread({
          channelId: payload.id,
          data: {
            user_treatment_id: Number(userTreatmentId),
          },
        }),
      )
      channelId.current = payload
      const countMessageRead =
        payload?.message_recipients_manager?.message_read ?? 0
      dispatch(actions.setCountMessageRead(countMessageRead))
    }
    const paramsMessage = {
      id: channelId.current.id,
      per_page: pagination.per_page,
      order: pagination.order,
      page: pagination.page,
      user_treatment_id: Number(userTreatmentId) || undefined,
    }
    dispatch(getMessagesRequest(paramsMessage))
  }

  const getChannelDetail = async () => {
    LocalStorage.setUserTreatmentId(userTreatmentId)
    const params = {
      user_treatment_id: userTreatmentId,
      with: 'managers',
    }
    const { payload } = await dispatch(getChannelRequest(params))

    return payload
  }

  const getUserTreatments = () => {
    const params: paramsType = {
      with: 'menu,lastDoctorFeedback',
      not_state: USER_TREATMENT_STATE.CANCELED,
      has_request_call: false,
    }

    dispatch(getUserTreatmentList(params))
  }

  const nextPage = () => {
    setPagination((prevState) => ({ ...prevState, page: prevState.page + 1 }))
  }

  // check prettier
  return (
    <Wrapper>
      <div className="chat-content">
        <div
          id="scrollableDiv"
          className="chat-content__message-area"
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(isFetchingChannel || isFetchingMessage) &&
            pagination.page === 1 && (
              <div className="chat-content__message-area__loading">
                <img src={Spinner} alt="loading" />
              </div>
            )}
          <InfiniteScroll
            dataLength={messages.length}
            next={nextPage}
            style={{ display: 'flex', flexDirection: 'column-reverse' }}
            inverse={true}
            hasMore={hasMore}
            loader={
              <div className="chat-content__loading">
                <img src={Spinner} alt="loading" />
              </div>
            }
            scrollableTarget="scrollableDiv"
          >
            {messages.map((item, i) => {
              return (
                <div key={item.id} ref={stwRefs[i]}>
                  <MessageItem item={item} />
                </div>
              )
            })}
          </InfiniteScroll>
        </div>
        <NewMessageForm />
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  flex-grow: 1;
  overflow: auto;
  .chat-content {
    display: flex;
    flex-direction: column;
    overflow: auto;
    height: 100%;

    &__message-area {
      flex-grow: 1;
      overflow: auto;
      padding: 15px;
      background: #fff;
      font-size: 14px;
      color: #444444;
      line-height: 25px;
      position: relative;

      &__loading {
        position: absolute;
        width: 100%;
        display: flex;
        height: 100%;
        align-items: center;
        justify-content: center;
        top: 0;
        left: 0;

        img {
          width: 35px;
          height: 35px;
        }
      }
    }

    &__loading {
      display: flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 10px;

      img {
        width: 35px;
        height: 35px;
      }
    }
  }
`

export default ChatContainer
